import PropTypes from "prop-types";
import React from "react";

import {
  Box,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { alpha, useTheme } from "@mui/material/styles";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { AdminRoutes } from "./routes/AdminRoutes";
import ApeakClub from "../../../../../images/apeak_club.png";

const BaseSideBar = ({
  userRole,
  open,
  isMobile,
  onToggleSidebar,
  LOGGED_USER_NAME,
  logout,
}) => {
  const theme = useTheme();
  const scrollbarTrack = alpha(
    theme.palette.mode === "dark"
      ? theme.palette.common.white
      : theme.palette.common.black,
    theme.palette.mode === "dark" ? 0.06 : 0.04,
  );
  const scrollbarThumb = alpha(
    theme.palette.mode === "dark"
      ? theme.palette.common.white
      : theme.palette.common.black,
    0.24,
  );
  const scrollbarThumbHover = alpha(
    theme.palette.mode === "dark"
      ? theme.palette.common.white
      : theme.palette.common.black,
    0.36,
  );

  const rotas = AdminRoutes;

  const location = useLocation();

  // Estilos reutilizáveis
  const selectedItemStyle = {
    minHeight: 48,
    justifyContent: open ? "initial" : "center",
    px: open ? 2.5 : 1,
    bgcolor: "primary.main",
    borderRadius: open ? "0 15px 15px 0" : "12px",
    "&:hover": { bgcolor: "primary.main" },
  };

  const normalItemStyle = {
    minHeight: 48,
    justifyContent: open ? "initial" : "center",
    px: open ? 2.5 : 1,
    borderRadius: open ? 0 : "12px",
  };

  const selectedIconStyle = {
    minWidth: 0,
    mr: open ? 3 : 0,
    justifyContent: "center",
    color: "primary.contrastText",
  };

  const normalIconStyle = {
    minWidth: 0,
    mr: open ? 3 : 0,
    justifyContent: "center",
  };

  const selectedTextStyle = {
    fontWeight: "bold",
    color: "primary.contrastText",
    opacity: open ? 1 : 0,
    display: open ? "block" : "none",
  };

  const normalTextStyle = {
    color: theme.palette.text.primary,
    opacity: open ? 1 : 0,
    display: open ? "block" : "none",
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflowX: "hidden",
      }}
    >
      {!isMobile && (
        <Box
          sx={{
            px: open ? 1.5 : 0,
            py: 1,
            display: "flex",
            justifyContent: open ? "flex-end" : "center",
          }}
        >
          <IconButton
            onClick={onToggleSidebar}
            aria-label={
              open ? "recolher menu lateral" : "expandir menu lateral"
            }
            size="small"
          >
            {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        </Box>
      )}

      <List
        sx={{
          flexGrow: 1,
          minHeight: 0,
          overflowY: "auto",
          scrollbarWidth: "thin",
          scrollbarColor: `${scrollbarThumb} ${scrollbarTrack}`,
          "&::-webkit-scrollbar": {
            width: 8,
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: scrollbarTrack,
            borderRadius: 8,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: scrollbarThumb,
            borderRadius: 8,
            border: `2px solid ${scrollbarTrack}`,
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: scrollbarThumbHover,
          },
        }}
      >
        {rotas.map((item) => (
          <React.Fragment key={item.full_name}>
            <Divider component="div" role="presentation">
              <Typography
                variant="caption"
                sx={{
                  display: open ? "block" : "none",
                }}
              >
                {item.full_name}
              </Typography>
            </Divider>

            {item.children.map((rota) => {
              const urlAtual = location.pathname;
              const isSelected = urlAtual.includes(rota.path);
              if (rota.hidden) {
                return null; // Ignora rotas ocultas
              }

              return (
                <ListItem
                  key={rota.name}
                  disablePadding
                  sx={{ display: "block" }}
                >
                  <ListItemButton
                    sx={isSelected ? selectedItemStyle : normalItemStyle}
                    component={Link}
                    to={rota.path}
                  >
                    <ListItemIcon
                      sx={isSelected ? selectedIconStyle : normalIconStyle}
                    >
                      {rota.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={rota.name}
                      sx={isSelected ? selectedTextStyle : normalTextStyle}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </React.Fragment>
        ))}
      </List>

      {/* Seção do usuário fixada na parte inferior */}
      {/* <Divider component="div" role="presentation"></Divider>
      <Typography
        variant="h6"
        textAlign="center"
        sx={{
          display: open ? "block" : "none",
          fontFamily: "'Audiowide', cursive",
          letterSpacing: 1,
          color: "primary.main",
          width: "100%",
          padding: 2,
        }}
      >
        {LOGGED_USER_NAME}
      </Typography> */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          pb: 2,
          pt: open ? 0 : 1,
        }}
      >
        <Box
          component="img"
          src={ApeakClub}
          alt="Logo Apeak"
          sx={{
            height: open ? 104 : 84,
            objectFit: "contain",
            opacity: 0.85,
          }}
        />
      </Box>
    </Box>
  );
};

BaseSideBar.propTypes = {
  LOGGED_USER_NAME: PropTypes.any,
  isMobile: PropTypes.bool,
  logout: PropTypes.func,
  onToggleSidebar: PropTypes.func,
  open: PropTypes.any,
  userRole: PropTypes.shape({
    toUpperCase: PropTypes.func,
  }),
};

export default BaseSideBar;
